import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import type { SubscriptionPriceProduct, SubscriptionProductType, SubscriptionType } from '@/models';
import {
  collectProductTypes,
  findSubscriptionProduct,
  getSubscriptionPeriodLabel,
  getSubscriptionTypeLabel,
  loadSubscriptionProducts,
  type SubscriptionProductTypeInfo,
} from '@/services/subscription-products';

interface SubscriptionProductsContextType {
  products: SubscriptionPriceProduct[];
  /** Alle Produktfamilien mit Anzeigename, abgeleitet aus `products`. */
  productTypes: SubscriptionProductTypeInfo[];
  loading: boolean;
  error: string | null;
  reload: () => Promise<void>;

  getProduct: (type: SubscriptionType) => SubscriptionPriceProduct | undefined;
  /** Anzeigename inkl. Laufzeit, z.B. "Cashflow (6M)". Unbekannte Typen liefern den Typ selbst. */
  getTypeLabel: (type: SubscriptionType) => string;
  /** Abrechnungszeitraum als Klartext, z.B. "Halb-Jährlich". */
  getPeriodLabel: (type: SubscriptionType) => string;
  getProductType: (type: SubscriptionType) => SubscriptionProductType | null;
  getProductTypeLabel: (productType: SubscriptionProductType) => string;
}

const SubscriptionProductsContext = createContext<SubscriptionProductsContextType | undefined>(undefined);

interface SubscriptionProductsProviderProps {
  children: ReactNode;
}

export function SubscriptionProductsProvider({ children }: SubscriptionProductsProviderProps) {
  const [products, setProducts] = useState<SubscriptionPriceProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchProducts = useCallback(async (force = false) => {
    try {
      setLoading(true);
      setError(null);
      setProducts(await loadSubscriptionProducts(force));
    } catch (err) {
      console.error('Failed to fetch subscription products:', err);
      setError('Die Produkte konnten nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchProducts();
  }, [fetchProducts]);

  const value = useMemo<SubscriptionProductsContextType>(() => {
    const getProduct = (type: SubscriptionType) => findSubscriptionProduct(products, type);
    const productTypes = collectProductTypes(products);

    return {
      products,
      productTypes,
      loading,
      error,
      reload:              () => fetchProducts(true),
      getProduct:          getProduct,
      getTypeLabel:        (type) => getSubscriptionTypeLabel(getProduct(type), type),
      getPeriodLabel:      (type) => getSubscriptionPeriodLabel(getProduct(type)),
      getProductType:      (type) => getProduct(type)?.productType ?? null,
      getProductTypeLabel: (productType) => productTypes.find(e => e.productType === productType)?.label ?? productType,
    };
  }, [products, loading, error, fetchProducts]);

  return (
    <SubscriptionProductsContext.Provider value={value}>
      {children}
    </SubscriptionProductsContext.Provider>
  );
}

export function useSubscriptionProducts(): SubscriptionProductsContextType {
  const context = useContext(SubscriptionProductsContext);
  if (context === undefined) {
    throw new Error('useSubscriptionProducts must be used within a SubscriptionProductsProvider');
  }
  return context;
}
